import { api } from "./api";

// Feature flag — when false, the existing direct-submit flow runs unchanged
// (keeps the live alpha working). Flip on together with the backend
// REQUIRE_VERIFIED_REGISTRATION once vdock-auth config + origin are live.
export const VERIFY_ENABLED = process.env.REACT_APP_REQUIRE_VERIFIED_REGISTRATION === "true";

// Members-only events ALWAYS need the verified flow, whatever the flag says:
// membership is only knowable from the live vdock-sync check inside
// /register/verify, so without it the backend can only ever answer 403. Pass the
// event (or series) and use this instead of VERIFY_ENABLED at every gate.
export const needsVerification = (ev) =>
  VERIFY_ENABLED || ev?.visibility === "members_only" || ev?.members_only === true;

const AUTH_URL = (process.env.REACT_APP_VDOCK_AUTH_URL || "").replace(/\/$/, "");
// DEV ONLY — skip the real vdock-auth request-code call so the flow runs locally
// without CORS/allowlist setup. Pair with backend AUTH_DEV_BYPASS. Code is 000000.
const DEV_BYPASS = process.env.REACT_APP_AUTH_DEV_BYPASS === "true";

// Step 1 — the BROWSER calls vdock-auth request-code directly (by design: each
// attendee spends their own per-IP budget; a backend call would share one bucket).
// Uses fetch, NOT the api client, so it doesn't carry our /api prefix or token.
export async function requestCode(email) {
  if (DEV_BYPASS) return true; // local mock — no vdock-auth call; enter code 000000
  if (!AUTH_URL) throw new Error("REACT_APP_VDOCK_AUTH_URL is not set");
  const res = await fetch(`${AUTH_URL}/auth/request-code`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, portal: "events" }),
  });
  if (res.status === 429) {
    let retry = 0;
    try { retry = (await res.json()).retry_after_seconds || 0; } catch (_) {}
    const e = new Error("rate_limited");
    e.retryAfter = retry;
    throw e;
  }
  if (!res.ok) throw new Error("request_failed");
  return true; // uniform success for any well-formed email
}

// Step 2 — our backend verifies the code, gates members-only (fail-closed), and
// mints the registration token + a short verified session. targetId is an event
// id/slug OR a series id.
export async function verifyRegistration(targetId, email, code) {
  const r = await api.post("/register/verify", { event_id: targetId, email, code });
  if (r.data?.sessionToken) setSession(r.data.sessionToken);
  return r.data; // { next, prefill, registrationToken, email, sessionToken } | { next: "members_only" } | { next: "already_registered", status }
}

// Deliberate sign-in (the My Events front door). The browser already requested
// the code from vdock-auth via requestCode(); this verifies it and stores a
// long-lived (30-day) session. Not tied to any event. Returns { email, isMember }.
export async function signIn(email, code) {
  const r = await api.post("/auth/signin/verify", { email, code });
  if (r.data?.sessionToken) setSession(r.data.sessionToken);
  return r.data;
}

/**
 * Member SSO bridge (M3): if the visitor is signed in to the members portal,
 * the shared .venturedock.com cookie rides along on same-origin requests and
 * the backend swaps it for our normal 30-day session — no magic code. Silent
 * by design: 204/failure means "not a portal member here", which is fine.
 * Only effective on events.venturedock.com (the cookie never reaches
 * *.onrender.com), and a no-op when a local session already exists.
 */
export async function bridgeSession() {
  if (getSession()) return false;
  try {
    const r = await api.post("/auth/bridge");
    if (r.status === 200 && r.data?.sessionToken) {
      setSession(r.data.sessionToken);
      return true;
    }
  } catch (_) {
    // Fall through to the normal flows; the bridge is convenience only.
  }
  return false;
}

// --- Short verified session ("logged in" for a while) -----------------------
const SESSION_KEY = "vd_events_session";
/** Fired whenever the verified session is created or cleared. */
export const SESSION_EVENT = "vd:session";
export function getSession() { try { return localStorage.getItem(SESSION_KEY) || null; } catch (_) { return null; } }
export function setSession(t) {
  try { if (t) localStorage.setItem(SESSION_KEY, t); } catch (_) {}
  try { window.dispatchEvent(new Event(SESSION_EVENT)); } catch (_) {}
}
export function clearSession() {
  try { localStorage.removeItem(SESSION_KEY); } catch (_) {}
  // Let the nav (and anything else showing verified state) update immediately,
  // rather than only on the next route change.
  try { window.dispatchEvent(new Event(SESSION_EVENT)); } catch (_) {}
}

/**
 * Who is currently verified, read from the stored token — no server round-trip.
 *
 * Decoding the JWT client-side is only for DISPLAY. The payload is base64, not
 * secret, and nothing here is trusted: every privileged action re-verifies the
 * token server-side, and membership is always re-checked live.
 *
 * Returns { email, expiresAt, minutesLeft } or null when absent/expired.
 */
export function getSessionInfo() {
  const t = getSession();
  if (!t) return null;
  try {
    const [, payload] = t.split(".");
    const claims = JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/")));
    const expiresAt = (claims.exp || 0) * 1000;
    if (!claims.email_normalized || !expiresAt || expiresAt <= Date.now()) {
      clearSession();               // expired — stop showing a stale identity
      return null;
    }
    const minutesLeft = Math.max(0, Math.round((expiresAt - Date.now()) / 60000));
    return {
      email: claims.email_normalized,
      isMember: !!claims.is_member,
      // 'signin' = a deliberate 30-day sign-in; 'verify' (or absent, for older
      // tokens) = the transient session minted while registering. Display only.
      scope: claims.scope || "verify",
      expiresAt,
      minutesLeft,
      daysLeft: Math.max(0, Math.round(minutesLeft / (60 * 24))),
    };
  } catch (_) {
    clearSession();
    return null;
  }
}

// Exchange the stored session for an event-bound registration token, skipping the
// code step. Returns the same shape as verifyRegistration, or null if there's no
// usable session (none stored, or expired — expired clears itself).
export async function resumeRegistration(targetId) {
  const s = getSession();
  if (!s) return null;
  try {
    const r = await api.post("/register/resume", { event_id: targetId, session_token: s });
    return r.data;
  } catch (e) {
    if (e.response?.status === 401) clearSession(); // expired/invalid session
    return null;
  }
}

// Full details of the signed-in attendee's registrations, for the My Events page.
// Returns { email, upcoming: [...], past: [...] }, or null with no usable session.
export async function myEventDetails() {
  const t = getSession();
  if (!t) return null;
  try {
    const r = await api.post("/registrations/mine/details", { session_token: t });
    return r.data;
  } catch (e) {
    if (e.response?.status === 401) clearSession();
    return null;
  }
}

// What has this visitor already signed up for? Used to badge the events list.
// Returns { events: {id: status}, series: {id: status} }, or null when there is
// no usable verified session (signed-out visitors simply see no badges).
export async function myRegistrations() {
  const t = getSession();
  if (!t) return null;
  try {
    const r = await api.post("/registrations/mine", { session_token: t });
    return r.data;
  } catch (e) {
    if (e.response?.status === 401) clearSession(); // expired
    return null;
  }
}
